import React, { useEffect, useMemo, useState } from "react";
import { useApi } from "../../utils/useApi"; // auto-refreshing fetch wrapper
import { useAuth } from "../../contexts/AuthContext";
// import { Table } from 'react-bootstrap';
import { Table, Button } from "react-bootstrap";
// import { Form } from 'react-bootstrap';
import { dedupedRequest } from "../../utils/axios";
import EditIcon from "../icons/EditIcon";
import DeleteIcon from "../icons/DeleteIcon";
import TaskModal from "./modals/TaskModal";
import { useNavigate } from "react-router-dom";

  type TaskPanelProps = {
    law_part_id: number;
    hideId?: boolean;
    showRegulationColumn?: boolean;
    highlightRegulationColumnOnly?: boolean;
    applyRowColorFromDeadline?: boolean;
    showAddButton?: boolean;
  };

const TasksPanel = (props: TaskPanelProps) => {
  const { token } = useAuth();
  const { apiFetch } = useApi();
  const navigate = useNavigate();
  // const [clients, setClients] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [activeTask, setActiveTask] = useState<any | null>(null);
  const law_part_id = props.law_part_id || 0;
  const hideId = props.hideId ?? false;
  const showRegulationColumn = props.showRegulationColumn ?? true;
  const highlightRegulationColumnOnly = props.highlightRegulationColumnOnly ?? false;
  const applyRowColorFromDeadline = props.applyRowColorFromDeadline ?? false;
  const showAddButton = props.showAddButton ?? false;
  // const [formData, setFormData] = useState({
  //   name: '',
  // });

  const loadTasks = async () => {
    console.log("Doing loadTasks");
    dedupedRequest({ method: "GET", url: `/tasks/${law_part_id}` }, { useCache: false })
    // dedupedRequest({ method: "GET", url: `/tasks` }, { useCache: false })
      .then((res) => setTasks(res.data))
      .catch((err) => console.error("Failed to load tasks", err));
  };

  const handleAddTask = () => {
    setActiveTask({
      id: -1,
      note: "",
      suggestion: "",
      deadline: "",
      responsible: "",
      origin: "",
      done: 0,
      comment: "",
      number_of_files: 0,
      files: [],
    });
  };

  const handleDeleteTask = (taskId: number) => {
    const files: boolean = tasks.find((t) => t.id === taskId)?.number_of_files > 0;
    if (files) {
      if (window.confirm("Diese Maßnahme hat noch angehängte Dateien. \nAlle angehängten Dateien werden ebenfalls gelöscht.\nMöchten Sie sie trotzdem löschen?")) {
        // User wants to delete the task, but we should also delete the files
        const filesToDelete = tasks.find((t) => t.id === taskId)?.files || [];
        Promise.all(filesToDelete.map((f: any) => 
          handleDeleteFile(taskId, f.id, false) // ask=false to skip confirmation for each file
        ))
        .then(() => {
          console.log("All files deleted successfully");
          // Now delete the task
          dedupedRequest({ method: "DELETE", url: `/task/${taskId}` })
            .then(() => {
              console.log("Task deleted successfully");
              loadTasks();
            })
            .catch((err) => console.error("Failed to delete task", err));
        })
        .catch((err) => console.error("Failed to delete files", err));
      }
    }
    deleteTask(taskId, !files); // If there are no files, we can ask for confirmation in deleteTask
  };

  const deleteTask = (taskId: number, ask: boolean = true) => {
    if (!ask || window.confirm("Möchten Sie diese Maßnahme wirklich löschen?")) {
      dedupedRequest({ method: "DELETE", url: `/task/${taskId}` })
        .then(() => {
          console.log("Task deleted successfully");
          loadTasks();
        })
        .catch((err) => console.error("Failed to delete task", err));
    }
  };

  const handleDeleteFile = (taskId: number, fileId: number, ask: boolean = true) => {
    if (!ask || window.confirm("Möchten Sie diese Datei wirklich löschen?")) {
      dedupedRequest({ method: "DELETE", url: `/task/${taskId}/file/${fileId}` })
        .then(() => {
          console.log("File deleted successfully");
          loadTasks();
        })
        .catch((err) => console.error("Failed to delete file", err));
    }
  };

  useEffect(() => {
    apiFetch(`/api/tasks/${law_part_id}`)
      .then(setTasks)
      .catch((err) =>
        console.error("Maßnahmen konnten nicht geladen werden", err),
      );
    apiFetch('/api/documents')
      .then(setDocuments)
      .catch((err) =>
        console.error("Dokumente konnten nicht geladen werden", err),
      );
  }, [apiFetch, token, law_part_id]);

  useEffect(() => {
    console.log("Active task changed:", activeTask);
  }, [activeTask]);

  const documentMap = useMemo(() => {
    const map = new Map<number, any>();
    documents.forEach((doc: any) => {
      if (doc?.law_part_id != null) {
        map.set(Number(doc.law_part_id), doc);
      }
      if (doc?.client_law_part_id != null) {
        map.set(Number(doc.client_law_part_id), doc);
      }
    });
    return map;
  }, [documents]);

  const getAssignedDocument = (task: any) => {
    const origin = Number(task?.origin ?? 0);
    const clientLawPartId = Number(task?.client_law_part_id ?? 0);
    const fallback = Number(task?.law_part_id ?? 0);
    return documentMap.get(origin) ?? documentMap.get(clientLawPartId) ?? documentMap.get(fallback) ?? null;
  };

  const getEvidenceLabel = (task: any) => {
    const required = Boolean(task?.evidence_required === true || task?.evidence_required === 1);
    const provided = Boolean(task?.evidence_provided === true || task?.evidence_provided === 1);

    if (!required && !provided) return "keine Nachweise erforderlich";
    if (required && !provided) return "Nachweise erforderlich";
    if (required && provided) return "Nachweise erbracht";
    return "keine Nachweise erforderlich";
  };

  const getDeadlineStatus = (done: number | boolean, deadline?: string | null): 'green' | 'orange' | 'red' => {
    // If task is done, return green
    if (done === 1 || done === true) {
      return 'green';
    }

    // If no deadline, return green
    if (!deadline) {
      return 'green';
    }

    const deadlineDate = new Date(deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    deadlineDate.setHours(0, 0, 0, 0);

    // If deadline is in the past, return red
    if (deadlineDate < today) {
      return 'red';
    }

    // Check if deadline is within one week
    const oneWeekFromNow = new Date(today);
    oneWeekFromNow.setDate(today.getDate() + 7);

    if (deadlineDate <= oneWeekFromNow) {
      return 'orange';
    }

    return 'green';
  };

  return (
    <>
      <h5 className="tasks-panel-heading">Maßnahmen</h5>
      <div className="tasks-header-row">
        {showAddButton && (
          <Button variant="outline-primary" size="sm" onClick={handleAddTask}>
            + Neue Maßnahme hinzufügen
          </Button>
        )}
        <span className="tasks-count">{tasks.length} Maßnahmen gefunden.</span>
      </div>
      <Table bordered className="tasks-dashboard-table" size="sm">
        <thead>
          <tr>
            {!hideId && <th>ID</th>}
            <th>Handlungsbedarf</th>
            <th>Umsetzungsvorschlag</th>
            <th>Durchführung</th>
            <th>Erledigt</th>
            <th>Anmerkungen</th>
            <th>Nachweise</th>
            <th>Dokumente</th>
            {showRegulationColumn && <th>Zugewiesene Vorschrift</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => {
            const assignedDoc = getAssignedDocument(t);
            const deadlineStatus = getDeadlineStatus(t.done, t.deadline);

            let rowStyle: React.CSSProperties | undefined = undefined;
            let rowClassName = "tasks-header";

            if (applyRowColorFromDeadline) {
              // Color whole row by deadline status
              const colors = {
                green: '#d4edda',
                orange: '#fff3cd',
                red: '#f8d7da'
              };
              rowStyle = { backgroundColor: colors[deadlineStatus] };
              rowClassName = "tasks-header tasks-row-deadline-colored";
            } else if (highlightRegulationColumnOnly && assignedDoc?.legal_area_color) {
              // Don't color the whole row, just the regulation column
              rowClassName = "tasks-header";
            } else {
              // No row coloring
              rowClassName = "tasks-header";
            }

            return (
              <React.Fragment key={t.id}>
                <tr className={rowClassName} style={rowStyle}>
                  {!hideId && <td>{t.id}</td>}
                  <td className="truncate-single">{t.note}</td>
                  <td className="truncate-single">{t.suggestion}</td>
                  <td className={applyRowColorFromDeadline ? '' : `deadline-cell deadline-status-${getDeadlineStatus(t.done, t.deadline)}`}>
                    <p>{t.deadline ? new Date(t.deadline).toLocaleDateString('de-DE') : ""}</p>
                    {/* <p>{t.deadline}</p> */}
                    <p>{t.responsible}</p>
                  </td>
                  {/* <td>{t.origin}</td> */}
                  {/* <td>{t.categories}</td> */}
                  {/* <td>{t.approval}</td> */}
                  <td>{t.done == 1 ? "Ja" : "Nein"}</td>
                  <td className="truncate-single">{t.comment}</td>
                  <td>{getEvidenceLabel(t)}</td>
                  <td>{t.number_of_files}</td>
                  {showRegulationColumn && (
                    <td className={highlightRegulationColumnOnly ? `regulation-cell regulation-color-${assignedDoc?.legal_area_color ? 'colored' : 'default'}` : ''} style={highlightRegulationColumnOnly && assignedDoc?.legal_area_color ? { backgroundColor: assignedDoc.legal_area_color } : undefined}>
                      {assignedDoc ? (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 tasks-regulation-link"
                          onClick={() => navigate(`/documents/${assignedDoc.law_part_id ?? assignedDoc.client_law_part_id ?? t.origin}`)}
                        >
                          {assignedDoc.law_name || 'Nicht zugewiesen'}
                        </Button>
                      ) : (
                        'Nicht zugewiesen'
                      )}
                    </td>
                  )}
                  <td className="actions actions-white">
                    <Button
                      className="action-btn"
                      size="sm"
                      onClick={() => setActiveTask(t)}
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      className="action-btn"
                      size="sm"
                      onClick={() => handleDeleteTask(t.id)}
                    >
                      <DeleteIcon />
                    </Button>
                  </td>
                </tr>
                {(t.number_of_files > 0) && (
                  <tr className='task-files'>
                    <td></td>
                    <td colSpan={hideId ? 9 : 8}>
                      <table className="table table-sm mb-0">
                        <thead>
                          <tr className='detail-header'>
                            <th>Bezeichnung</th>
                            <th>Datei</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {t.files.map((f: any) => (
                            <tr key={f.id} className='detail-row'>
                              <td className="truncate-single">{f.description}</td>
                              <td>
                                <a
                                  href={`/api/tasks/${t.id}/files/${f.id}/download`}
                                  // target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {f.file_name}
                                </a>
                              </td>
                              <td>
                                <Button
                                  className="action-btn"
                                  size="sm"
                                  onClick={() => handleDeleteFile(t.id, f.id)}
                                >
                                  <DeleteIcon />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      {activeTask && (
        <TaskModal
          show={true}
          TaskId={activeTask.id}
          law_part_id={law_part_id}
          t={activeTask}
          onClose={() => setActiveTask(null)}
          onSave={loadTasks}
        />
      )}
    </>
  );
};

export default TasksPanel;
